import type { AuthAdapter, UniversalUser } from '@/app/_auth/auth-adapter'
import { AuthProviderID } from '@/app/_auth/steps/_common/login-buttons'
import type { User as FirebaseUser } from 'firebase/auth'
import Keycloak from 'keycloak-js'

let keycloakInstance: Keycloak | null = null
let keycloakInitPromise: Promise<Keycloak> | null = null

type KeycloakConfig = {
  url: string
  realm: string
  clientId: string
}

type KeycloakConfigFile = {
  vpc: KeycloakConfig
}

/**
 * Fetch Keycloak configuration from JSON file
 */
async function fetchKeycloakConfig(): Promise<KeycloakConfig> {
  const response = await fetch('/public/keycloak-config.json', {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    throw new Error('FAILED_TO_FETCH_KEYCLOAK_CONFIG_JSON')
  }

  const data = (await response.json()) as KeycloakConfigFile

  // Currently only supporting VPC mode with JSON config
  if (!data.vpc) {
    throw new Error('KEYCLOAK_VPC_CONFIG_NOT_FOUND')
  }

  return data.vpc
}

/**
 * Initialize Keycloak instance (singleton, ensures single initialization)
 */
async function initKeycloak(): Promise<Keycloak> {
  if (keycloakInstance) {
    return keycloakInstance
  }

  if (keycloakInitPromise) {
    return keycloakInitPromise
  }

  keycloakInitPromise = (async () => {
    try {
      // Fetch configuration from JSON file
      const config = await fetchKeycloakConfig()

      const { url, realm, clientId } = config

      if (!url || !realm || !clientId) {
        const missing = []
        if (!url) missing.push('url')
        if (!realm) missing.push('realm')
        if (!clientId) missing.push('clientId')
        throw new Error(`Missing required Keycloak configuration fields: ${missing.join(', ')}`)
      }

      const keycloak = new Keycloak({
        url,
        realm,
        clientId,
      })

      const authenticated = await keycloak.init({
        onLoad: 'check-sso',
        pkceMethod: 'S256',
        checkLoginIframe: false,
        flow: 'standard',
        responseMode: 'query',
      })

      // If authenticated on init (e.g., after redirect), ensure token is fresh
      if (authenticated && keycloak.token) {
        // Token should already be available, but ensure it's valid
        try {
          await keycloak.updateToken(30)
        } catch (tokenError) {
          console.warn('Failed to update token on init:', tokenError)
        }
      }

      console.log(
        `Keycloak authenticated: %c${authenticated}`,
        `background:${authenticated ? 'green' : 'red'}; font-weight: bold;padding:2px 4px;border-radius: 4px;color:white;`,
      )

      console.log(
        '%cKeycloak token:',
        'background:gold; font-weight: bold;padding:2px 4px;border-radius: 4px;color: black;',
        keycloak.token,
      )

      keycloakInstance = keycloak
      return keycloak
    } catch (error) {
      // Reset promise on error so initialization can be retried
      keycloakInitPromise = null

      // Ensure we always throw a proper Error object
      if (error instanceof Error) {
        throw error
      }

      const errorMessage =
        error === null || error === undefined
          ? 'Keycloak initialization failed with unknown error'
          : typeof error === 'string'
            ? error
            : String(error)

      throw new Error(errorMessage)
    }
  })()

  return keycloakInitPromise
}

/**
 * Get the Keycloak instance (returns null if not initialized)
 */
export function getKeycloak(): Keycloak | null {
  return keycloakInstance
}

/**
 * Keycloak authentication adapter
 */
class KeycloakAuthAdapter implements AuthAdapter {
  private authStateCallbacks: Array<(user: UniversalUser | null) => void> = []
  private initialized = false

  async initialize(): Promise<void> {
    if (this.initialized) {
      // If already initialized, check if auth state changed (e.g., after redirect)
      this.notifyAuthStateChange()
      return
    }

    try {
      const keycloak = await initKeycloak()

      // Set up auth state change handlers BEFORE checking state
      // This ensures callbacks are registered when state changes
      keycloak.onAuthSuccess = () => {
        this.notifyAuthStateChange()
      }
      keycloak.onAuthLogout = () => {
        this.notifyAuthStateChange()
      }
      keycloak.onTokenExpired = () => {
        keycloak.updateToken(30).catch((error) => {
          console.error('Failed to refresh token')
        })
      }

      this.initialized = true

      // Notify initial state immediately after initialization
      // This is critical when coming back from Keycloak redirect
      // The authenticated state should already be detected by keycloak.init()
      this.notifyAuthStateChange()

      // Also notify after a short delay to catch any async state updates
      // This handles cases where Keycloak processes the redirect asynchronously
      setTimeout(() => {
        this.notifyAuthStateChange()
      }, 200)
    } catch (err) {
      let errorMessage = 'Unknown error'
      if (err instanceof Error) {
        errorMessage = err.message || 'Unknown error'
      } else if (typeof err === 'string') {
        errorMessage = err
      } else if (err !== null && err !== undefined) {
        errorMessage = String(err)
      }
      console.error('Failed to initialize Keycloak:', errorMessage, err)
      throw err instanceof Error ? err : new Error(errorMessage)
    }
  }

  async signIn(providerId: AuthProviderID): Promise<void> {
    // Ensure initialization is complete before attempting to sign in
    if (!this.initialized) {
      await this.initialize()
    }

    const keycloak = getKeycloak()
    if (!keycloak) {
      const error = new Error('Keycloak not initialized. Please check your Keycloak configuration.')
      throw error
    }

    try {
      // Use current URL as redirect to preserve any query params like ?domain=xxx
      const redirectUri = window.location.origin + window.location.pathname
      await keycloak.login({ redirectUri })
    } catch (error) {
      throw error
    }
  }

  async signOut(): Promise<void> {
    // Ensure initialization is complete
    if (!this.initialized) {
      await this.initialize()
    }

    const keycloak = getKeycloak()
    if (!keycloak) {
      const error = new Error('Keycloak not initialized')
      throw error
    }

    try {
      await keycloak.logout({ redirectUri: window.location.origin })
    } catch (error) {
      throw error
    }
  }

  getUserId(): string | null {
    return getKeycloak()?.tokenParsed?.sub ?? null
  }

  async getToken(): Promise<string> {
    const keycloak = getKeycloak()
    if (!keycloak?.authenticated || !keycloak.tokenParsed) {
      const error = new Error('KEYCLOAK_NOT_AUTHENTICATED')
      throw error
    }

    try {
      await keycloak.updateToken(30)
      if (!keycloak.token) {
        const error = new Error('FAILED_TO_GET_KEYCLOAK_TOKEN')
        throw error
      }
      console.log('Keycloak token:', keycloak.token)
      return keycloak.token
    } catch (error) {
      throw error
    }
  }

  getCurrentUser(): FirebaseUser | null {
    // Keycloak doesn't have a Firebase User object
    // Return null as this is Firebase-specific
    return null
  }

  async authStateReady(): Promise<void> {
    // Keycloak auth state is ready after initialization
    // No additional waiting needed
  }

  /**
   * Convert Keycloak token data to UniversalUser
   */
  private convertToUniversalUser(): UniversalUser | null {
    const keycloak = getKeycloak()
    const tokenParsed = keycloak?.tokenParsed

    if (!keycloak?.authenticated || !tokenParsed) {
      return null
    }

    return {
      uid: tokenParsed.sub ?? '',
      email: (tokenParsed.email as string | undefined) ?? null,
      displayName: (tokenParsed.name as string | undefined) ?? null,
      photoURL: null, // Keycloak tokens typically don't include photo URL
    }
  }

  onAuthStateChanged(callback: (user: UniversalUser | null) => void): () => void {
    this.authStateCallbacks.push(callback)

    // Call immediately with current state
    // If not initialized yet, wait for initialization
    if (!this.initialized) {
      this.initialize()
        .then(() => {
          const user = this.convertToUniversalUser()
          callback(user)
        })
        .catch(() => {
          callback(null)
        })
    } else {
      const user = this.convertToUniversalUser()
      callback(user)
    }

    // Return unsubscribe function
    return () => {
      const index = this.authStateCallbacks.indexOf(callback)
      if (index > -1) {
        this.authStateCallbacks.splice(index, 1)
      }
    }
  }

  isAuthenticated(): boolean {
    return getKeycloak()?.authenticated ?? false
  }

  private notifyAuthStateChange(): void {
    const user = this.convertToUniversalUser()
    this.authStateCallbacks.forEach((callback) => callback(user))
  }
}

export { KeycloakAuthAdapter }
